<div class="card booking-card">
  <div class="card-header booking-card__header">
    <div>
      <div class="booking-card__eyebrow">Task Management</div>
      <h3 class="sub-title mb-0">
        <i class="bi bi-check2-square me-2 text-primary"></i>Staff Daily Tasks
      </h3>
    </div>

    @if (isLinkedToClinic) {
      <button
        class="btn btn-sm btn-primary"
        [disabled]="!isClinicActive"
        [class.opacity-50]="!isClinicActive"
        [attr.title]="!isClinicActive ? 'Not available until clinic is active' : null"
        (click)="openCreateForm()"
      >
        <i class="bi bi-plus-lg me-1"></i>New Task
      </button>
    }
  </div>

  <div class="card-body p-3">
    @if (isLoading) {
      <div class="text-center py-4">
        <div class="spinner-border spinner-border-sm text-primary"></div>
        <div class="mt-2 text-muted small">Loading tasks...</div>
      </div>
    }

    @if (showCreateForm) {
      <div class="card border p-3 mb-3 bg-light rounded-3">
        <h6 class="fw-bold text-primary mb-3">Add New Task</h6>

        <div class="mb-2">
          <label class="form-label">Task Title <span class="text-danger">*</span></label>
          <input
            class="form-control form-control-sm"
            [(ngModel)]="newTodo.title"
            placeholder="e.g. Follow up on blood test results"
          />
        </div>

        <div class="mb-2">
          <label class="form-label">Description (Optional)</label>
          <textarea
            class="form-control form-control-sm"
            rows="2"
            [(ngModel)]="newTodo.description"
            placeholder="Additional details..."
          >
          </textarea>
        </div>

        <div class="row g-2 mb-3">
          <div class="col-6">
            <label class="form-label">Due Date</label>
            <input
              type="date"
              class="form-control form-control-sm"
              [(ngModel)]="newTodo.todoDate"
            />
          </div>

          <div class="col-6">
            <label class="form-label">Priority</label>
            <select class="form-select form-select-sm" [(ngModel)]="newTodo.priority">
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
            </select>
          </div>
        </div>

        <!-- Recurring Task Toggle -->
        <div class="mb-3 p-2 rounded-2" style="background: #f0f7ff; border: 1px solid #bfe7ff">
          <div class="form-check form-switch mb-0">
            <input
              class="form-check-input"
              type="checkbox"
              id="recurringToggle"
              [checked]="isRecurringTask"
              (change)="toggleRecurringMode()"
            />
            <label class="form-check-label fw-semibold" for="recurringToggle">
              <i
                class="bi bi-arrow-repeat me-1"
                [style.color]="isRecurringTask ? '#0d6efd' : '#999'"
              ></i>
              Recurring Task
            </label>
            @if (isRecurringTask) {
              <small class="d-block text-muted mt-1 ms-4"
                >This task will repeat automatically</small
              >
            }
          </div>

          @if (isRecurringTask) {
            <div class="mt-3 ms-4">
              <!-- Frequency Dropdown -->
              <div class="mb-2">
                <label class="form-label small fw-semibold">Repeat</label>
                <select class="form-select form-select-sm" [(ngModel)]="newTodo.recurrencePattern">
                  @for (freq of recurrenceFrequencies; track freq) {
                    <option [value]="freq">{{ frequencyLabels[freq] }}</option>
                  }
                </select>
              </div>

              <!-- Optional End Date -->
              <div class="mb-0">
                <label class="form-label small fw-semibold">End Date (Optional)</label>
                <input
                  type="date"
                  class="form-control form-control-sm"
                  [(ngModel)]="newTodo.recurrenceEndDate"
                  [min]="getMinEndDate()"
                  placeholder="Leave blank for indefinite"
                />
                <small class="text-muted d-block mt-1">Leave blank to repeat indefinitely</small>
              </div>
            </div>
          }
        </div>

        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-sm btn-outline-secondary" (click)="cancelCreate()">Cancel</button>
          <button
            class="btn btn-sm btn-primary"
            (click)="saveTodo()"
            [disabled]="!newTodo.title || isSaving"
          >
            <i class="bi bi-check-lg me-1"></i>{{ isSaving ? 'Saving...' : 'Save Task' }}
          </button>
        </div>
      </div>
    }

    @if (!isLoading && todos.length === 0) {
      <div class="empty-state empty-state--compact">
        <i class="bi bi-check2-circle fs-2 d-block mb-1 text-primary"></i>
        <h6>All caught up!</h6>
        <p class="text-muted small mb-0">No pending staff tasks for today.</p>
      </div>
    }

    <div class="d-flex flex-column gap-2">
      @for (todo of todos; track todo.id) {
        <div class="todo-item p-3 rounded-3 border" [class.todo-completed]="todo.completed">
          <div class="d-flex justify-content-between align-items-start gap-2">
            <div class="flex-grow-1 min-w-0">
              <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                <span
                  class="fw-bold todo-title"
                  [class.text-decoration-line-through]="todo.completed"
                >
                  {{ todo.title }}
                </span>

                @if (todo.isRecurring) {
                  <span
                    class="badge bg-info-subtle text-info border border-info-subtle rounded-pill small"
                  >
                    <i class="bi bi-arrow-repeat me-1"></i>Repeating
                    {{ frequencyLabels[todo.recurrencePattern || 'DAILY'] | lowercase }}
                  </span>
                }

                @if (todo.completed) {
                  <span
                    class="badge bg-success-subtle text-success border border-success-subtle rounded-pill"
                  >
                    <i class="bi bi-check-circle-fill me-1"></i>Completed
                  </span>
                }
              </div>

              @if (todo.description) {
                <div class="text-muted small mb-2">
                  {{ todo.description }}
                </div>
              }

              <div class="d-flex gap-2 align-items-center flex-wrap">
                <span
                  class="badge rounded-pill"
                  [ngClass]="{
                    'bg-danger-subtle text-danger border border-danger-subtle':
                      todo.priority === 'HIGH',
                    'bg-warning-subtle text-warning border border-warning-subtle text-dark':
                      todo.priority === 'MEDIUM',
                    'bg-success-subtle text-success border border-success-subtle':
                      todo.priority === 'LOW',
                  }"
                >
                  {{ todo.priority }}
                </span>

                <small class="text-muted">
                  <i class="bi bi-calendar-event me-1"></i>
                  {{ todo.todoDate | date: 'dd MMM yyyy' }}
                </small>
              </div>
            </div>

            @if (!todo.completed) {
              <div class="dropdown">
                <button
                  class="btn btn-sm btn-light border"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i class="bi bi-three-dots-vertical"></i>
                </button>

                <ul class="dropdown-menu dropdown-menu-end shadow-sm">
                  <li>
                    <button class="dropdown-item" (click)="markComplete(todo.id)">
                      <i class="bi bi-check-circle me-2 text-success"></i>
                      Mark as Complete
                    </button>
                  </li>
                  <li>
                    <button class="dropdown-item text-danger" (click)="deleteTodo(todo.id)">
                      <i class="bi bi-trash me-2"></i>
                      Delete
                    </button>
                  </li>
                </ul>
              </div>
            }
          </div>
        </div>
      }
    </div>
  </div>
</div>
